<template>
  <div>
    <div
      v-if="!videoStore.isVideoLoaded || videoStore.isCategoryChanging"
      class="py-10 flex justify-center"
    >
      <BaseSpinner />
    </div>
    <div v-else-if="videoStore.video">
      <article class="flex flex-col gap-6 mb-6 text-gray-900">
        <div class="flex flex-col gap-2 p-4 sm:p-8 bg-white rounded-xl">
          <BaseRatio
            :ratio="9 / 16"
            class="bg-black rounded-xl overflow-hidden mb-2"
          >
            <iframe
              v-if="videoStore.video.video_source !== VideoSource.WNMeet"
              class="w-full h-full rounded-xl"
              :src="videoUrl"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              credentialless
              anonymous
            />
            <video v-else class="w-full h-full rounded-xl" controls>
              <source
                :src="videoStore.video?.temp_video_url"
                type="video/mp4"
              />
            </video>
          </BaseRatio>
          <div class="flex justify-between">
            <div
              v-if="categories.length"
              class="flex flex-wrap items-center gap-1"
            >
              <BaseLocaleLink
                v-for="item in categories"
                :key="item.id"
                :to="item.route"
                class="no-underline font-normal"
                :class="item.hidden && 'pointer-events-none'"
              >
                <BaseButton
                  size="xs"
                  look="none"
                  class="bg-primary-100 hover:bg-primary-400"
                >
                  {{ item.local }}
                </BaseButton>
              </BaseLocaleLink>
            </div>
            <BaseButton
              look="link"
              theme="dark"
              class="font-normal inline-flex"
              size="xs"
              @click="goBackToVideos"
            >
              <BaseIcon name="outline_arrow_turn_left" size="xs" class="mr-2" />
              <span class="text-sm">
                {{ $t('video.back_to_videos') }}
              </span>
            </BaseButton>
          </div>
          <h2 class="text-2xl text-gray-900 font-bold">
            {{ videoStore.video.title }}
          </h2>
          <div
            v-if="videoStore.video.date"
            class="flex gap-2 text-gray-900 items-center"
          >
            <BaseIcon name="outline_calendar" size="xs" class="flex-shrink-0" />
            <div>
              {{ getHumanizedDate(videoStore.video.date) }}
            </div>
          </div>
          <div v-if="event">
            <BaseLink
              v-if="event.slug"
              :to="getEventPageRoute(event.slug, event.type)"
            >
              <div class="flex gap-2 items-center">
                <BaseIcon
                  name="outline_external_link"
                  size="xs"
                  class="flex-shrink-0 text-primary"
                />
                <div>
                  {{ event.name }}
                  <span v-if="event.date_from && event.date_to">
                    -
                    {{
                      getHumanizedDatePeriod(
                        event.date_from,
                        event.date_to,
                        event.timezone
                      )
                    }}
                  </span>
                </div>
              </div>
            </BaseLink>
            <div v-else class="flex gap-2 items-center">
              <BaseIcon
                name="outline_ticket"
                size="xs"
                class="flex-shrink-0 mt-[1px]"
              />
              <div class="font-semibold">
                {{ event.name }}
              </div>
            </div>
          </div>
          <div
            v-if="videoStore.video.speakers?.length"
            class="flex gap-6 flex-wrap leading-5"
          >
            <template
              v-for="speaker in videoStore.video.speakers"
              :key="speaker.id"
            >
              <UsersUserAvatar
                :src="getAvatar(speaker)"
                :abbr="getAbbr(speaker)"
                size="lg"
                class="w-max"
              >
                <template v-if="speaker?.is_subscription_active" #topBadge>
                  <SubscriptionsProBadge />
                </template>
                <template #title>
                  <BaseLocaleLink
                    v-if="authStore.auth"
                    :to="`/users/${speaker.id}`"
                    class="hover:text-gray-700 font-medium"
                  >
                    {{ getFullName(speaker) }}
                  </BaseLocaleLink>
                  <span v-else class="font-medium">
                    {{ getFullName(speaker) }}
                  </span>
                </template>
                <template v-if="speaker.job" #text>
                  <div class="text-xs text-gray-500">
                    {{ speaker.job }}
                  </div>
                </template>
              </UsersUserAvatar>
            </template>
          </div>
          <CardActionsPanel
            class="!px-0 mt-4"
            :name="videoStore.video.title"
            :comments="commentsCounter"
            :reactions="reactionsCounters"
            :page-link="pageLink"
            :is-counters-processing="isCountersProcessing"
            @change-reaction="onChangeReaction"
          />
        </div>
        <div class="flex flex-col gap-2 bg-white rounded-xl p-4 sm:p-8">
          <div class="marked" v-html="$marked(videoStore.video?.description)" />
        </div>
      </article>
      <div class="flex gap-6 flex-col lg:flex-row">
        <div class="flex-1">
          <div ref="commentsElement" class="p-4 sm:p-8 bg-white rounded-xl">
            <CommentsList
              :id="id"
              :total="commentsCounter"
              :entity-type="EntityType.Video"
              @loaded="scrollToComments"
              @create="onCommentCreate"
            />
          </div>
        </div>
        <div class="flex flex-col gap-2 w-full lg:w-1/3 xl:w-1/4 px-4 sm:px-0">
          <div class="text-lg font-bold text-gray-900">
            {{ $t('video.related') }}
          </div>
          <div
            v-if="!videoStore.isRelatedVideoListLoaded"
            class="py-10 flex justify-center"
          >
            <BaseSpinner />
          </div>
          <VideoRelatedList v-else :video-list="videoStore.relatedVideoList">
            <template #empty>
              <span class="text-gray-700">
                {{ $t('video.no_related') }}
              </span>
            </template>
          </VideoRelatedList>
          <BaseLink
            v-if="isShowRelatedLink"
            class="mt-2"
            :to="{ name: 'video-category', params: { category: categorySlug } }"
          >
            {{ $t('video.all_related_videos') }}
          </BaseLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import { VideoSource } from '~core/plugins/helpers/videoHelper'
import { useVideo } from '~/stores/video'
import { EntityType } from '~/models/common'
import { useNavigation } from '~/stores/navigation'
import { useAuth } from '~/stores/auth'
import { useEvents } from '~/stores/events'

const videoStore = useVideo()
const route = useRoute()
const { getCategoryItemsByIds, getPageLinkCategorySlug } = useNavigation()
const authStore = useAuth()
const host = useHost()
const { getEventPageRoute } = useEvents()
const router = useRouter()
const { getHumanizedDate, getHumanizedDatePeriod } = useDate()
const nuxtApp = useNuxtApp()

const id = Array.isArray(route.params.id)
  ? Number(route.params.id[0])
  : Number(route.params.id)

const categories = computed(() =>
  getCategoryItemsByIds(
    videoStore.video?.categories.map((category) => category.id) || [],
    'video'
  )
)

const categorySlug = computed(() => getPageLinkCategorySlug(categories.value))
const pageLink = computed(() => {
  return videoStore.getVideoPageLink(id, categorySlug.value)
})

const videoUrl = computed(() => {
  if (!videoStore.video) {
    return ''
  }

  const {
    video_source: videoSource,
    video_id: videoId,
    video_url: videoUrl,
  } = videoStore.video

  return (
    (videoStore.video &&
      videoStore.getVideoSrc({ videoSource, videoId, videoUrl, host })) ||
    ''
  )
})

const fetchRelatedVideoList = async () => {
  if (videoStore.video?.event.id) {
    await videoStore.fetchRelatedVideoList(
      Number(categories.value[0].id),
      videoStore.video.id
    )
  }
}

const entity = {
  entity_id: id,
  entity_type: EntityType.Video,
}

const {
  initCountersHelper,
  commentsCounter,
  reactionsCounters,
  updateCommentsCounter,
  onChangeReaction,
  isCountersProcessing,
} = useCountersHelper()

onServerPrefetch(async () => {
  await videoStore.fetchVideo(id)
  nuxtApp.runWithContext(() => initMeta())
  await fetchRelatedVideoList()
})

onMounted(async () => {
  await videoStore.fetchVideo(id)
  initMeta()
  !useNuxtApp().isHydrating && (await fetchRelatedVideoList())
  await initCountersHelper({
    entity,
    isLoadComments: true,
    isLoadReactions: true,
  })
})

const commentsElement = ref<HTMLElement | null>(null)
function scrollToComments() {
  route.query.scroll === 'comments' && commentsElement.value?.scrollIntoView()
}

function onCommentCreate() {
  updateCommentsCounter(commentsCounter.value + 1)
}

const event = computed(() => videoStore.video?.event)

const categoriesNames = computed(() =>
  categories.value.map((item) => item.local)
)
const tagsNames = computed(
  () => videoStore.video?.tags.map((item) => item.name) || []
)

const initMeta = () => {
  if (videoStore.video) {
    useHeadMeta(
      {
        title: videoStore.video.meta_title || videoStore.video.title.trim(),
        description:
          videoStore.video.meta_description || videoStore.video.title.trim(),
        keywords: videoStore.video.meta_keywords || tagsNames.value.join(', '),
        image: `https://img.youtube.com/vi/${videoStore.video.video_id}/hqdefault.jpg`,
        article: {
          publishedTime: videoStore.video.date,
          sections: categoriesNames.value,
          tags: tagsNames.value,
        },
      },
      { canonical: pageLink }
    )
  }
}

const isShowRelatedLink = computed(
  () =>
    videoStore.isRelatedVideoListLoaded && videoStore.relatedVideoList.length
)

const goBackToVideos = () => {
  const prevRoute = router.options.history.state.back?.toString() || ''

  if (prevRoute.includes('video') && !prevRoute.includes('item-')) {
    router.back()
  } else {
    router.push(useLocaleRoute()({ name: 'video' }) as RouteLocationRaw)
  }
}

watch(
  () => !!authStore.auth,
  async () => {
    videoStore.resetVideo()
    await videoStore.fetchVideo(id)
    fetchRelatedVideoList()
  }
)
</script>
